/**
 * Circuit Breaker for fds-id API calls
 *
 * Provides resilient fallback when fds-id server is unavailable.
 * After threshold failures, the circuit opens and immediately falls back
 * to local operations without attempting server calls.
 *
 * Pattern: After 5 failures within 30s, circuit opens for 30s, then half-opens to test.
 */

import type { CircuitBreakerState } from './types'

const DEFAULT_THRESHOLD = 5
const DEFAULT_RESET_TIMEOUT_MS = 30000

export class FdsIdCircuitBreaker {
  private failures = 0
  private lastFailure = 0
  private readonly threshold: number
  private readonly resetTimeout: number

  constructor(threshold = DEFAULT_THRESHOLD, resetTimeoutMs = DEFAULT_RESET_TIMEOUT_MS) {
    this.threshold = threshold
    this.resetTimeout = resetTimeoutMs
  }

  /**
   * Execute a function with circuit breaker protection
   * Returns fallback result if circuit is open or call fails
   */
  async call<T>(fn: () => Promise<T>, fallback: () => T | Promise<T>): Promise<T> {
    if (this.isOpen()) {
      console.info('[fds-id] Circuit breaker open, using local fallback')
      return fallback()
    }

    try {
      const result = await fn()
      this.reset()
      return result
    } catch (error) {
      this.recordFailure()
      console.warn('[fds-id] Call failed, using local fallback:', error)
      return fallback()
    }
  }

  /**
   * Check if circuit breaker is currently open
   */
  isOpen(): boolean {
    if (this.failures >= this.threshold) {
      // Check if we should half-open (reset timeout elapsed)
      if (Date.now() - this.lastFailure > this.resetTimeout) {
        // Half-open: allow one request through to test
        this.failures = 0
        return false
      }
      return true
    }
    return false
  }

  /**
   * Record a failure
   */
  private recordFailure(): void {
    this.failures++
    this.lastFailure = Date.now()
  }

  /**
   * Reset the circuit breaker after successful call
   */
  private reset(): void {
    this.failures = 0
  }

  /**
   * Get current circuit breaker state (for debugging/monitoring)
   */
  getState(): CircuitBreakerState {
    return {
      failures: this.failures,
      lastFailure: this.lastFailure,
      isOpen: this.isOpen(),
    }
  }
}

/**
 * Helper to retry with exponential backoff before circuit breaker trips
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  maxRetries = 3,
  baseDelayMs = 1000
): Promise<T> {
  let lastError: Error | undefined

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error))
      if (i < maxRetries - 1) {
        // Exponential backoff: 1s, 2s, 4s
        await sleep(Math.pow(2, i) * baseDelayMs)
      }
    }
  }

  throw lastError
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

// Singleton circuit breaker instance
let circuitBreakerInstance: FdsIdCircuitBreaker | null = null

export function getCircuitBreaker(): FdsIdCircuitBreaker {
  if (!circuitBreakerInstance) {
    circuitBreakerInstance = new FdsIdCircuitBreaker()
  }
  return circuitBreakerInstance
}

export function resetCircuitBreaker(): void {
  circuitBreakerInstance = null
}
